/**
 * AdminContent
 *
 * Body of the AdminSheet. Renders a vertical list of tappable admin tool rows
 * (icon + title + description) via the shared `ListItemTile` primitive, so it
 * looks like a visual sibling of `HelpAboutSheet` / `AccountInfoSheet`.
 *
 * rendering at lines 101-172, the per-item dispatch at lines 62-85). Tapping
 * the "Latest users" row navigates to the standalone `LatestUsersPage`
 * (`/admin/latest-users`) via `Navigator.navigateToLatestUsers()`, porting
 * Flutter's `latestUsers` -> `LatestUsersScreen` navigation. The Feedback row
 * navigates to the users-feedback list page (`Navigator.navigateToUsersFeedback()`,
 * Flutter's `NavigationService.navigateToUsersFeedbackScreen()`), and the
 * Transactions row opens the wallet page in its admin variant
 * (`Navigator.navigateToWallet({ isFromAdmin: true })`, admin_screen.dart:79-83).
 * The remaining `IDENTITY_VERIFICATION` row stays a placeholder that opens the
 * "Not implemented yet" InfoSheet until its Flutter destination is ported.
 *
 * Per-item icon tint mirrors `admin_screen.dart:136-146`: Flutter renders the
 * identity-verification icon with `color: null` and every other icon with
 * `AppColors.disabledColor`. On web the identity row uses the default
 * `ListItemTile` tint (no explicit `iconColor`) and the other three use
 * `theme.disabled`. See the file's `Deviations` note in task_4_plan.md re: the
 * mask-based `AssetIcon` not supporting a truly full-color asset.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { Navigator } from '@/presentation/routing/Navigator';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';
import { AdminItemType } from '@/domain/enums/admin/adminItemType';
import { useAdminItems } from '../hooks/useAdminItems';

export interface AdminContentProps {
  /** Screen key forwarded to nested sheets (e.g., InfoSheet) */
  screenKey?: string;
}

export const AdminContent: React.FC<AdminContentProps> = ({ screenKey }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const items = useAdminItems();

  // Web port of `_onItemTap` (admin_screen.dart:62-85). Navigating from within
  // the admin sheet does not need an explicit close: the PUSH hides the sheet
  // behind the new route and a POP back auto-dismisses it (ExpauseSheet route
  // listener). The remaining `IDENTITY_VERIFICATION` row stays on the
  // "Not implemented yet" placeholder until its feature lands.
  const handleItemClick = (type: AdminItemType) => {
    if (type === AdminItemType.LATEST_USERS) {
      Navigator.navigateToLatestUsers();
      return;
    }
    if (type === AdminItemType.FEEDBACK) {
      Navigator.navigateToUsersFeedback();
      return;
    }
    if (type === AdminItemType.TRANSACTIONS) {
      Navigator.navigateToWallet({ isFromAdmin: true });
      return;
    }
    InfoSheet.showNotImplementedYet({ l10n, screenKey });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: responsive.spacing.xl,
        paddingRight: responsive.spacing.xl,
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {items.map((item) => (
        <ListItemTile
          key={item.type}
          title={item.title}
          description={item.description}
          iconPath={item.iconPath}
          // Flutter tints every icon `AppColors.disabledColor` except the
          // identity-verification icon (`color: null`). On web the identity
          // row keeps the default `ListItemTile` tint; the rest use disabled.
          iconColor={
            item.type === AdminItemType.IDENTITY_VERIFICATION ? undefined : theme.disabled
          }
          onClick={() => handleItemClick(item.type)}
          qa={{ id: `admin-${item.type}` }}
        />
      ))}
    </div>
  );
};
